/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { DetailsRow, IColumn, IDetailsHeaderProps, IDetailsRowProps, IDetailsRowStyles } from '@fluentui/react/lib/DetailsList';
import { IDropdownOption, IDropdownProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { IStackProps, IStackSlots, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { IStyle } from '@fluentui/react/lib/Styling';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { CluPrebuiltEntity } from 'src/clu/core/types/CluPrebuiltEntity';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { Table } from 'src/core/components/Table';
import { SortKeyType, TableProps } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { PrebuiltEntityCapability, SublistEntityCapability } from 'src/core/types/EntityStore';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

export type CluEntityPrebuiltCapabilityTableProps = TableProps<PrebuiltEntityCapability> & {
    arePrebuiltsLoading: boolean;
    allPrebuilts: CluPrebuiltEntity[];
    dismissEditingPrebuilt: () => void;
    prebuiltBeingEdited: PrebuiltEntityCapability;
    onEditPrebuilt: (prebuilt: PrebuiltEntityCapability) => void;
    onAddPrebuilt: (prebuilt: PrebuiltEntityCapability) => void;
};

type PrebuiltInEditModeProps = {
    columns: IColumn[];
    onDismiss: () => void;
    projectCulture?: string;
    rowProps?: IDetailsRowProps;
    arePrebuiltsLoading?: boolean;
    allPrebuilts: CluPrebuiltEntity[];
    initialPrebuilt?: PrebuiltEntityCapability;
    entityPrebuilts: PrebuiltEntityCapability[];
    onSubmit: (prebuilt: PrebuiltEntityCapability) => void;
};

const messages = defineMessages({
    prebuiltName: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.prebuiltName',
        defaultMessage: 'Prebuilt name'
    },
    description: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.description',
        defaultMessage: 'Description'
    },
    language: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.language',
        defaultMessage: 'Language'
    },
    selectPrebuilt: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.selectPrebuilt',
        defaultMessage: 'Select prebuilt'
    },
    addNewPrebuilt: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.addNewPrebuilt',
        defaultMessage: 'Add new prebuilt'
    },
    searchPrebuilts: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.searchPrebuilts',
        defaultMessage: 'Search prebuilts'
    },
    save: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.save',
        defaultMessage: 'Save'
    },
    cancel: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.cancel',
        defaultMessage: 'Cancel'
    },
    example: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.example',
        defaultMessage: 'Example'
    },
    notFound: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.notFound',
        defaultMessage: 'No result found'
    },
    maximumNumberOfPrebuilts: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.maximumNumberOfPrebuilts',
        defaultMessage: 'Maximum number of allowed prebuilts is 7'
    },
    prebuiltDropDownAriaLabel: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.prebuiltDropDownAriaLabel',
        defaultMessage: 'Prebuilts dropdown'
    }
});

const { styleComponent } = getStylistV2('CluEntityPrebuiltCapabilityTable');
const scopedTheme = getScopedTheme('CluEntityPrebuiltCapabilityTable');

const rowStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { borderBottom: `${scopedTheme.borderColor} solid 1px`, height: '42px', paddingLeft: '52px' }
});

const prebuiltOptionStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { borderBottom: `${scopedTheme.borderColor} solid 1px`, width: '100%' }
});

const dropdownItemStyles: IStyle = { minHeight: 56, lineHeight: 16, padding: '29px 8px' };
const prebuiltsDropdownStyles = classNamesFunction<IDropdownProps, IDropdownStyles>()({
    dropdownItem: dropdownItemStyles,
    dropdownItemDisabled: dropdownItemStyles,
    dropdownItemSelected: dropdownItemStyles,
    dropdownItemSelectedAndDisabled: dropdownItemStyles
});

const RowContainer = styleComponent<IStackProps>(Stack)(
    'RowContainer',
    (_, cssVars) => ({
        backgroundColor: cssVars.defaultBackground,
        borderBottom: `${scopedTheme.focussingColor} solid 1px`,
        $nest: { '&:hover': { backgroundColor: cssVars.hoveringBackground } }
    }),
    {
        cssVars: { hoveringBackground: '', defaultBackground: '' },
        cssVarsSetter: ({ isSelected, isNewPrebuilt }: { isSelected: boolean; isNewPrebuilt: boolean }) => ({
            hoveringBackground: `${isSelected ? scopedTheme.focussingColor : scopedTheme.hoveringColor}`,
            defaultBackground: `${isNewPrebuilt ? scopedTheme.focussingColor : isSelected ? scopedTheme.hoveringColor : 'unset'}`
        })
    }
);

const detailsRowStyles = classNamesFunction<IDetailsRowProps, IDetailsRowStyles>()({
    root: {
        borderBottom: 'none',
        backgroundColor: 'transparent !important'
    },
    cell: { overflow: 'visible' }
});

const isSamePrebuilt = (
    prebuilt1: CluPrebuiltEntity | PrebuiltEntityCapability,
    prebuilt2: CluPrebuiltEntity | PrebuiltEntityCapability
): boolean => prebuilt1.category === prebuilt2.category;

export const PrebuiltInEditMode = (props: PrebuiltInEditModeProps) => {
    const { columns, allPrebuilts, entityPrebuilts, initialPrebuilt, onSubmit, onDismiss, rowProps, arePrebuiltsLoading = false } = props;

    const defaultPrebuilt: PrebuiltEntityCapability = {
        id: getId(),
        category: ''
    };

    const { localize } = useLocalization();

    const [prebuilt, setPrebuilt] = useState<PrebuiltEntityCapability>(initialPrebuilt ?? defaultPrebuilt);
    const [hasPrebuiltBeenModified, setHasPrebuiltBeenModified] = useState<boolean>(false);

    const isSelected = rowProps?.selection.getSelectedIndices().includes(rowProps?.itemIndex);

    const isNewPrebuilt = !initialPrebuilt;
    const isSaveButtonDisabled = isNewPrebuilt ? !prebuilt.category : !hasPrebuiltBeenModified;

    const getColumnContent = (columnKey: string): (() => JSX.Element) => {
        switch (columnKey) {
            case 'category':
                const prebuiltOptions: IDropdownOption[] = allPrebuilts.map(p => ({
                    key: p.category,
                    text: p.category,
                    disabled: Boolean(entityPrebuilts.find(ep => isSamePrebuilt(ep, p))),
                    data: {
                        description: p.description,
                        example: p.examples[0]
                    }
                }));

                return () => (
                    <LuisDropdown
                        required
                        maxHeight={300}
                        options={prebuiltOptions}
                        isLoading={arePrebuiltsLoading}
                        styles={prebuiltsDropdownStyles}
                        key={`${prebuilt.id}-${columnKey}`}
                        ariaLabel={localize(messages.prebuiltDropDownAriaLabel)}
                        onRenderOption={option =>
                            option && option.data ? (
                                <Stack styles={prebuiltOptionStyles}>
                                    <LuisText>{option.text}</LuisText>
                                    <TruncatedText size="12px" color={scopedTheme.secondaryColor} title={option.data.description}>
                                        {localize(messages.description)}
                                        :&nbsp;
                                        {option.data.description}
                                    </TruncatedText>
                                    <TruncatedText size="12px" color={scopedTheme.secondaryColor} title={option.data.example}>
                                        {localize(messages.example)}
                                        :&nbsp;
                                        {option.data.example}
                                    </TruncatedText>
                                </Stack>
                            ) : (
                                <TruncatedText size="16px" color={scopedTheme.secondaryColor} styles={{ root: { fontStyle: 'italic' } }}>
                                    {localize(messages.notFound)}
                                </TruncatedText>
                            )
                        }
                        selectedKey={prebuilt.category}
                        placeholder={localize(messages.selectPrebuilt)}
                        onChange={(_, option) => {
                            setPrebuilt({ id: prebuilt.id, category: option.key as string, ...option.data });
                            setHasPrebuiltBeenModified(true);
                        }}
                    />
                );
            default:
                return () => null;
        }
    };

    return (
        <RowContainer key={`prebuilt-${prebuilt.category}`} customProps={{ isSelected, isNewPrebuilt }}>
            {isNewPrebuilt ? (
                <Stack horizontal grow tokens={{ padding: '0 0 0 48px' }}>
                    {columns.map(column => (
                        <Stack.Item
                            key={column.key}
                            styles={{
                                root: { width: column.calculatedWidth, padding: '11px 8px 11px 12px' }
                            }}>
                            {getColumnContent(column.key)()}
                        </Stack.Item>
                    ))}
                </Stack>
            ) : (
                <DetailsRow
                    {...rowProps}
                    styles={detailsRowStyles}
                    columns={columns.map(c => ({ ...c, onRender: getColumnContent(c.key) }))}
                />
            )}

            <Stack horizontal verticalAlign="center" horizontalAlign="end" tokens={{ childrenGap: 4, padding: 8 }}>
                <PrimaryButton text={localize(messages.save)} disabled={isSaveButtonDisabled} onClick={() => onSubmit(prebuilt)} />
                <DefaultButton text={localize(messages.cancel)} onClick={onDismiss} />
            </Stack>
        </RowContainer>
    );
};

/**
 * A component displaying all prebuilts in an entity's prebuilt capability.
 */
export const CluEntityPrebuiltCapabilityTable = observer((props: CluEntityPrebuiltCapabilityTableProps) => {
    const {
        items,
        allPrebuilts,
        arePrebuiltsLoading,
        prebuiltBeingEdited,
        selection,
        dismissEditingPrebuilt,
        onAddPrebuilt,
        onEditPrebuilt
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const [sortKey, setSortKey] = React.useState<SortKeyType<PrebuiltEntityCapability>>(null);
    const [isAddingNewPrebuilt, setIsAddingNewPrebuilt] = useState<boolean>();

    const { currentCluProjectStore: currentProject } = rootStore;

    const isMultiLingualProject = currentProject.isMultiLingual;

    const handleSubmitNewPrebuilt = (prebuilt: PrebuiltEntityCapability) => {
        onAddPrebuilt(prebuilt);
        setIsAddingNewPrebuilt(false);
        logButtonClick(serviceName, 'Schema', 'AddPrebuilt', 'EntityComponents', currentProject.id);
    };

    const onRenderTableHeader = (headerProps: IDetailsHeaderProps, defaultRenderer: (props?: IDetailsHeaderProps) => JSX.Element) => (
        <Stack>
            {defaultRenderer(headerProps)}

            <StackItem styles={rowStyles}>
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canWrite || isAddingNewPrebuilt}
                    data-automation-id="addNewPrebuiltButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <LuisTooltip
                            hidden={items.length < LuisConstants.Clu.MaxNumOfPrebuiltEntitiesPerCompositeEntity}
                            content={localize(messages.maximumNumberOfPrebuilts)}>
                            <ActionButton
                                iconProps={{ iconName: 'Add' }}
                                text={localize(messages.addNewPrebuilt)}
                                disabled={
                                    !canWrite ||
                                    isAddingNewPrebuilt ||
                                    items.length >= LuisConstants.Clu.MaxNumOfPrebuiltEntitiesPerCompositeEntity
                                }
                                onClick={() => setIsAddingNewPrebuilt(true)}
                            />
                        </LuisTooltip>
                    }
                />
            </StackItem>

            {isAddingNewPrebuilt && (
                <PrebuiltInEditMode
                    columns={headerProps.columns}
                    projectCulture={!isMultiLingualProject && rootStore.currentCluProjectStore.culture}
                    allPrebuilts={allPrebuilts}
                    entityPrebuilts={items}
                    onSubmit={handleSubmitNewPrebuilt}
                    arePrebuiltsLoading={arePrebuiltsLoading}
                    onDismiss={() => setIsAddingNewPrebuilt(false)}
                />
            )}
        </Stack>
    );

    const onRenderTableRow = (rowProps: IDetailsRowProps, defaultRenderer: (props?: IDetailsRowProps) => JSX.Element) => {
        const isInEditMode = (rowProps.item as SublistEntityCapability).id === prebuiltBeingEdited?.id;

        return isInEditMode ? (
            <PrebuiltInEditMode
                projectCulture={!isMultiLingualProject && rootStore.currentCluProjectStore.culture}
                initialPrebuilt={rowProps.item as PrebuiltEntityCapability}
                columns={rowProps.columns}
                allPrebuilts={allPrebuilts}
                entityPrebuilts={items}
                onSubmit={onEditPrebuilt}
                onDismiss={dismissEditingPrebuilt}
                arePrebuiltsLoading={arePrebuiltsLoading}
                rowProps={rowProps}
            />
        ) : (
            defaultRenderer(rowProps)
        );
    };

    const onSortColumn = (column: IColumn) => {
        setSortKey({ key: column.key as keyof PrebuiltEntityCapability, order: column?.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    const columns: IColumn[] = [
        {
            key: 'category',
            minWidth: 230,
            maxWidth: 250,
            isRowHeader: true,
            fieldName: 'category',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.prebuiltName),
            isSorted: sortKey?.key === 'category',
            isSortedDescending: sortKey?.key === 'category' && sortKey?.order === 'desc'
        },
        {
            minWidth: 150,
            maxWidth: 200,
            key: 'description',
            isResizable: true,
            isRowHeader: false,
            isCollapsible: true,
            name: localize(messages.description),
            onRender: (item: PrebuiltEntityCapability) => (
                <LuisText>{allPrebuilts.find(p => isSamePrebuilt(p, item))?.description || '--'}</LuisText>
            )
        }
    ];

    return (
        <Table<PrebuiltEntityCapability>
            {...props}
            items={items}
            columns={columns}
            onRenderRow={onRenderTableRow}
            onSortColumn={onSortColumn}
            isLoading={arePrebuiltsLoading}
            onRenderDetailsHeader={onRenderTableHeader}
        />
    );
});
